import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQueryClient } from 'react-query'
import { Button } from '@farewill/ui'
import {
  type DisbursementBreakdownItem,
  type ProbateQuoteHistoryAttributes,
} from 'lib/models/probate-quote-history'
import type { ProbateCase } from 'lib/models/probate-case'
import { INVOICE_LINE_ITEMS } from 'utils/enums'
import { formatCurrency, hasDecimals } from 'lib/formatting/currency'
import { totalPriceFromDisbursementBreakdown } from './helpers'
import useApi from 'lib/effects/api'
import { RESOURCES } from 'api'
import styled from 'styled-components'
import { COLOR, FONT, GTR, BORDER_RADIUS } from '@farewill/ui/tokens'
import { PriceRowAttributes, PriceTable, StyledPrice } from './price-table'

const StyledMessageWrapper = styled.div`
  background: ${COLOR.ACCENT.PRIMARY_20};
  border: 1px solid ${COLOR.ACCENT.PRIMARY_120};
  margin-bottom: ${GTR.S};
  padding-left: ${GTR.S};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${GTR.S};
  border-radius: ${BORDER_RADIUS.S};
  color: ${COLOR.BLACK};
`

const StyledTotalSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${GTR.XS};
  padding-top: ${GTR.M};
  text-align: right;
`

const StyledTotalLabel = styled.span`
  font-size: 24px;
  font-weight: ${FONT.WEIGHT.BOLD};
  color: ${COLOR.GREY.DARK};
`

type PriceBreakdownProps = {
  quotedPrice: number
  disbursementBreakdown: ProbateQuoteHistoryAttributes['disbursementBreakdown']
  isDisabled?: boolean
  version: string
  discount?: number
  isOutdatedVersion?: boolean
  latestQuoteVersion?: string
}

const formatBreakdownItemPrice = (item: DisbursementBreakdownItem) =>
  formatCurrency({
    value: item.price,
    valueInPence: true,
    showPence: hasDecimals(item.price / 100),
  })

export const FinalPriceBreakdown = ({
  disbursementBreakdown = [],
  isDisabled = false,
  version,
  quotedPrice,
  discount,
  isOutdatedVersion = false,
  latestQuoteVersion,
}: PriceBreakdownProps) => {
  const probateCase = useSelector(
    (state: { probateCase: ProbateCase }) => state.probateCase
  )
  const cache = useQueryClient()
  const [, makeRequest] = useApi()
  const [isApplying, setIsApplying] = useState(false)

  const handleApplyToQuote = async () => {
    if (!latestQuoteVersion) return

    setIsApplying(true)
    try {
      await makeRequest({
        url: `/api/probate-cases/${probateCase.id}/quote`,
        method: 'POST',
        data: {
          data: {
            type: 'probate_case_quotes',
            attributes: { quoteVersion: latestQuoteVersion },
          },
        },
      })

      cache.invalidateQueries([
        RESOURCES.INVOICES,
        `filter[probateCaseId]=${probateCase.id}`,
      ])
      cache.invalidateQueries([RESOURCES.PROBATE_QUOTE_HISTORY, probateCase.id])
    } finally {
      setIsApplying(false)
    }
  }

  const showOutdatedVersionBanner = isOutdatedVersion && !isDisabled

  const findItem = (description: string) =>
    disbursementBreakdown.find((item) => item.description === description)

  const courtFeeItem = findItem(INVOICE_LINE_ITEMS.COURT_FEE)
  const disbursementsItem = findItem(INVOICE_LINE_ITEMS.DISBURSEMENTS)
  const grantCopiesItem = findItem(INVOICE_LINE_ITEMS.GRANT_COPIES)
  const bankruptcySearchFeeItem = findItem(
    INVOICE_LINE_ITEMS.BANKRUPTCY_SEARCH_FEE
  )
  const financialAssetSearchItem = findItem(
    INVOICE_LINE_ITEMS.FINANCIAL_ASSET_SEARCH
  )
  const legalNoticeFeeLondonGazetteItem = findItem(
    INVOICE_LINE_ITEMS.LEGAL_NOTICE_FEE_LONDON_GAZETTE
  )
  const legalNoticeFeeLocalNewspaperItem = findItem(
    INVOICE_LINE_ITEMS.LEGAL_NOTICE_FEE_LOCAL_NEWSPAPER
  )
  const amlCheckItem = findItem(INVOICE_LINE_ITEMS.AML_CHECK)

  const finalPrice = totalPriceFromDisbursementBreakdown({
    disbursementBreakdown,
    quotedPrice,
    discount: discount || 0,
  })

  // Build disbursement items array
  const disbursementItems: PriceRowAttributes[] = []

  if (courtFeeItem) {
    disbursementItems.push({
      name: 'Court fee',
      price: formatBreakdownItemPrice(courtFeeItem),
      quantity: courtFeeItem.quantity || 0,
    })
  }

  if (grantCopiesItem) {
    disbursementItems.push({
      name: 'Copies of the Grant',
      price: `${formatBreakdownItemPrice(grantCopiesItem)} per copy`,
      quantity: grantCopiesItem.quantity || 0,
    })
  }

  if (disbursementsItem) {
    disbursementItems.push({
      name: 'Disbursements',
      price: formatBreakdownItemPrice(disbursementsItem),
      quantity: disbursementsItem.quantity || 0,
    })
  }
  if (bankruptcySearchFeeItem) {
    disbursementItems.push({
      name: 'Bankruptcy search fee',
      price: `${formatBreakdownItemPrice(bankruptcySearchFeeItem)} per search`,
      quantity: bankruptcySearchFeeItem.quantity || 0,
    })
  }
  if (financialAssetSearchItem) {
    disbursementItems.push({
      name: 'Financial asset search (excl. VAT)',
      price: formatBreakdownItemPrice(financialAssetSearchItem),
      quantity: financialAssetSearchItem.quantity || 0,
    })
  }
  if (legalNoticeFeeLondonGazetteItem) {
    disbursementItems.push({
      name: 'Legal notice fee - London Gazette (excl. VAT)',
      price: formatBreakdownItemPrice(legalNoticeFeeLondonGazetteItem),
      quantity: legalNoticeFeeLondonGazetteItem.quantity || 0,
    })
  }
  if (legalNoticeFeeLocalNewspaperItem) {
    disbursementItems.push({
      name: 'Legal notice fee - Local newspaper (excl. VAT)',
      price: formatBreakdownItemPrice(legalNoticeFeeLocalNewspaperItem),
      quantity: legalNoticeFeeLocalNewspaperItem.quantity || 0,
    })
  }
  if (amlCheckItem) {
    disbursementItems.push({
      name: 'AML check',
      price: `${formatBreakdownItemPrice(
        amlCheckItem
      )} per acting PR and beneficiary`,
      quantity: amlCheckItem.quantity || 0,
    })
  }

  // Don't render the table if there are no disbursement items
  if (disbursementItems.length === 0) {
    return (
      <StyledTotalSection>
        <StyledTotalLabel>Total price:</StyledTotalLabel>
        <StyledPrice>
          {formatCurrency({
            value: finalPrice,
            showPence: hasDecimals(finalPrice),
          })}
        </StyledPrice>
      </StyledTotalSection>
    )
  }

  return (
    <>
      {showOutdatedVersionBanner && (
        <StyledMessageWrapper>
          <span>
            A new version of disbursements is available ({latestQuoteVersion})
          </span>
          <Button.Underline onClick={handleApplyToQuote} loading={isApplying}>
            Apply to quote
          </Button.Underline>
        </StyledMessageWrapper>
      )}
      <PriceTable
        rows={disbursementItems}
        withQuantity
        title="Disbursements breakdown"
        isDisabled={isDisabled}
        version={version}
      />
      <StyledTotalSection>
        <StyledTotalLabel>Total price:</StyledTotalLabel>
        <StyledPrice>
          {formatCurrency({
            value: finalPrice,
            showPence: hasDecimals(finalPrice),
          })}
        </StyledPrice>
      </StyledTotalSection>
    </>
  )
}
